import React, { useCallback, useContext } from "react";

import { PickerDate } from "@euroland/calendar-react";
import { Button } from "@euroland/base-ui";
import Label from "../ui/Label";
import NumberInput from "../ui/NumberInput";
import Controller from "../Form/Controller";
import { classNames } from "../../utils";
import { useCompanySettings, useFormatSetting, useSelectedInstrument } from "../../hooks/useCompanySettings";
import QuadrataSquareCheckbox from "../ui/Checkbox/QuadrataSquareCheckbox";
import i18n from "../../locale/i18n";
import { useBreakpoint } from "../../hooks/useResize";
import withAmountInvestedSelection from "../../hocs/withAmountInvestedSelection";
import { isUseLatinNumber } from "../../store/utils";
import { SHORT_DATE_FORMAT, TAB } from "../../constant/common";
import { Dropdown, Option } from "@euroland/dropdown-react";
import { AmountInvestedSelectContext } from "../../context/AmountAndShareSelectContext";
import useCurrencyName from "../../hooks/useCurrencyName";

const QuadrataAmountInvestedSelection = ({
  onSubmit,
  formState,
  enabledDividendOption,
  loading,
  minimumPriceLoading,
  resolveAmountInvestedErrorMessage,
  minStartDate,
  maxStartDate,
  minEndDate,
  maxEndDate,
}) => {
  const { tab } = useContext(AmountInvestedSelectContext);
  const companySettings = useCompanySettings();
  const [, { formatNumber }] = useSelectedInstrument();

  const { isMobileScreen } = useBreakpoint();
  const format = useFormatSetting();
  const { setFieldValue, getFieldValue } = formState;
  const getCurrencyNameByCode = useCurrencyName();

  const handleFormatNumber = useCallback(value => formatNumber(value), [formatNumber]);
  const calendarFormat = format.calendarDate || format.shortDate || SHORT_DATE_FORMAT;
  const isRtl = window.appSettings.isRtl;
  const amountInvestedErrorMessage = resolveAmountInvestedErrorMessage(formState.error?.["amountInvested"]);

  return (
    <>
      <div className="quadrata-selection__wrapper">
        <div className="quadrata-selection__row">
          <div className=" quadrata-selection__item">
            <Label
              htmlFor="dateOfInvestment"
              className={classNames({ "d-hidden-accessibility": tab !== TAB.AMOUNT_INVESTED })}
            >
              {i18n.translate("dateOfInvestment")}
            </Label>
            <PickerDate
              id="dateOfInvestment"
              dateFormat={calendarFormat}
              className={classNames("quadrata-selection__input", {
                left: isRtl,
                right: !isRtl,
              })}
              value={getFieldValue("startDate")}
              align={isMobileScreen ? "right" : "left"}
              minDate={minStartDate}
              maxDate={maxStartDate}
              onChange={date => setFieldValue("startDate", date)}
              textCancel={i18n.translate("cancel")}
              textApply={i18n.translate("apply")}
            />
          </div>
          <div className="quadrata-selection__item">
            <Label
              htmlFor="endDateOfInvestment"
              className={classNames({ "d-hidden-accessibility": tab !== TAB.AMOUNT_INVESTED })}
            >
              {i18n.translate("endDateOfInvestment")}
            </Label>
            <PickerDate
              id="endDateOfInvestment"
              dateFormat={calendarFormat}
              align="right"
              className={classNames("quadrata-selection__input", {
                left: isRtl,
                right: !isRtl,
              })}
              value={getFieldValue("endDate")}
              minDate={minEndDate}
              maxDate={maxEndDate}
              onChange={date => setFieldValue("endDate", date)}
              textCancel={i18n.translate("cancel")}
              textApply={i18n.translate("apply")}
            />
          </div>
        </div>
        <div
          className="quadrata-selection__row"
          style={companySettings.currencyEnabled ? undefined : { rowGap: 0 }}
        >
          <div className="quadrata-selection__item">
            <Label htmlFor="amountInvested">{i18n.translate("amountInvestedInput")}</Label>
            <NumberInput
              useLatinNumbers={isUseLatinNumber}
              className={classNames({ error: (loading === false) && (minimumPriceLoading === false) && !!formState.error?.["amountInvested"] }, "quadrata-selection__input")}
              title={i18n.translate("amountInvested")}
              value={getFieldValue("amountInvested")}
              onChange={value => setFieldValue("amountInvested", value)}
              formatNumber={handleFormatNumber}
              require
              id="amountInvested"
              errorMessage={loading || minimumPriceLoading ? undefined : amountInvestedErrorMessage}
            />
          </div>
          <div className="quadrata-selection__item">
            {companySettings.currencyEnabled && tab === TAB.AMOUNT_INVESTED && (
              <>
                <Label>{i18n.translate("chooseCurrency")}</Label>
                <Dropdown
                  icon={<span className="fs-triangle-down fs-icon"></span>}
                  value={getFieldValue("currency")}
                  className="quadrata-selection__input"
                  placeholder={i18n.translate("localCurrency")}
                  onOptionSelect={value => setFieldValue("currency", value)}
                  dropdownMenuLabel="dropdown currency"
                  aria-label={i18n.translate("chooseCurrency")}
                >
                  <Option value="">{i18n.translate("localCurrency")}</Option>
                  {companySettings.currencies.map(({ code, text }) => (
                    <Option key={code} value={code} aria-label={getCurrencyNameByCode(code)}>
                      {text || code}
                    </Option>
                  ))}
                </Dropdown>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="quadrata-selection__action">
        {enabledDividendOption && tab === TAB.AMOUNT_INVESTED && (
          <QuadrataSquareCheckbox
            className="quadrata-selection__ratio"
            selected={getFieldValue("dividendChecked")}
            onSelect={() => setFieldValue("dividendChecked", !getFieldValue("dividendChecked"))}
          />
        )}

        <Button
          outline
          className="quadrata-selection__submit button--outline"
          onClick={onSubmit}
          loading={loading}
          disabled={minimumPriceLoading}
        >
          <span className="button-text">{i18n.translate("showData")}</span>
          <span className="fs-icon fs-right-dash-arrow"></span>
        </Button>
      </div>
    </>
  );
};

export default withAmountInvestedSelection(QuadrataAmountInvestedSelection);
